/**
 * Email Templates Page
 *
 * Super Admin page for managing transactional email template overrides.
 * Super Admins can customize the HTML, subject, and preheader of any of
 * the 13 platform transactional emails. Customized templates override the
 * static TypeScript defaults at send time.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { cn, Button } from '@respectly/ui';
import {
  ArrowLeft,
  RefreshCw,
  Mail,
  Pencil,
  CheckCircle2,
  AlertCircle,
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  Building2,
  Users,
} from 'lucide-react';
import type { EmailTemplateKey, EmailTemplateListItem } from '@respectly/shared-types';
import { EMAIL_TEMPLATE_DEFINITIONS } from '@respectly/shared-types';
import {
  useEmailTemplatesStore,
  useEmailTemplatesByCategory,
} from '../../stores/useEmailTemplatesStore';
import { EmailTemplateEditorPanel } from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
};

// ============================================
// Category Filter Tabs
// ============================================

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'auth', label: 'Auth' },
  { value: 'billing', label: 'Billing' },
  { value: 'team', label: 'Team' },
  { value: 'documents', label: 'Documents' },
  { value: 'support', label: 'Support' },
] as const;

// ============================================
// Template Card
// ============================================

interface TemplateCardProps {
  template: EmailTemplateListItem;
  onEdit: (key: EmailTemplateKey) => void;
}

function TemplateCard({ template, onEdit }: TemplateCardProps) {
  const categoryColors: Record<string, string> = {
    auth: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    billing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    team: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    documents: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400',
    support: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  };

  return (
    <motion.div
      variants={cardVariants}
      className="group relative rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm transition-all duration-200"
    >
      {/* Top row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            {template.name}
          </span>
          <span
            className={cn(
              'text-[11px] font-medium px-2 py-0.5 rounded-full capitalize',
              categoryColors[template.category] ?? categoryColors.auth
            )}
          >
            {template.category}
          </span>
        </div>

        {/* Edit button (visible on hover) */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEdit(template.key)}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-150 shrink-0 h-7 text-xs"
        >
          <Pencil className="w-3 h-3 mr-1" />
          Edit
        </Button>
      </div>

      {/* Subject preview */}
      <p className="text-xs text-slate-500 dark:text-slate-400 truncate mb-3">
        Subject: {template.customSubject ?? template.defaultSubject}
      </p>

      {/* Bottom row: status + variables */}
      <div className="flex items-center gap-2 flex-wrap">
        {template.isCustomized ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
            <CheckCircle2 className="w-3 h-3" />
            Customized
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
            Default
          </span>
        )}

        <span className="text-[11px] text-slate-400 dark:text-slate-500">
          {template.variableCount} variable{template.variableCount !== 1 ? 's' : ''}
        </span>

        {template.isCustomized && template.updatedAt && (
          <span className="ml-auto text-[11px] text-slate-400 dark:text-slate-500">
            Updated {new Date(template.updatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </motion.div>
  );
}

// ============================================
// Sender Group Section
// ============================================

interface SenderGroupProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  accentClass: string;
  templates: EmailTemplateListItem[];
  isOpen: boolean;
  onToggle: () => void;
  onEdit: (key: EmailTemplateKey) => void;
  isLoading: boolean;
}

function SenderGroup({
  title,
  description,
  icon,
  accentClass,
  templates,
  isOpen,
  onToggle,
  onEdit,
  isLoading,
}: SenderGroupProps) {
  const customizedCount = templates.filter((t) => t.isCustomized).length;

  return (
    <motion.div
      variants={itemVariants}
      className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      {/* Group Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left"
      >
        <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', accentClass)}>
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {title}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
              {templates.length} template{templates.length !== 1 ? 's' : ''}
            </span>
            {customizedCount > 0 && (
              <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                {customizedCount} customized
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
            {description}
          </p>
        </div>
        {isOpen ? (
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
        )}
      </button>

      {/* Group Content */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-4">
              {isLoading && templates.length === 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 animate-pulse"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                        <div className="h-4 w-16 bg-slate-100 dark:bg-slate-800 rounded-full" />
                      </div>
                      <div className="h-3 w-48 bg-slate-100 dark:bg-slate-800 rounded mb-3" />
                      <div className="h-4 w-24 bg-slate-100 dark:bg-slate-800 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : templates.length > 0 ? (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
                >
                  {templates.map((template) => (
                    <TemplateCard key={template.key} template={template} onEdit={onEdit} />
                  ))}
                </motion.div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
                    <LayoutGrid className="w-5 h-5 text-slate-400" />
                  </div>
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                    No templates in this category
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================
// Main Page Component
// ============================================

export function EmailTemplates() {
  const {
    isLoadingList,
    listError,
    selectedKey,
    editorTemplate,
    isLoadingDetail,
    isSaving,
    isResetting,
    isSendingTest,
    loadTemplates,
    loadTemplate,
    saveTemplate,
    resetTemplate,
    sendTestEmail,
  } = useEmailTemplatesStore();

  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [respectlyGroupOpen, setRespectlyGroupOpen] = useState(true);
  const [customerGroupOpen, setCustomerGroupOpen] = useState(true);

  const filteredTemplates = useEmailTemplatesByCategory(selectedCategory);

  const respectlyTemplates = filteredTemplates.filter(
    (t) => EMAIL_TEMPLATE_DEFINITIONS[t.key].senderType === 'respectly'
  );
  const customerTemplates = filteredTemplates.filter(
    (t) => EMAIL_TEMPLATE_DEFINITIONS[t.key].senderType === 'customer'
  );

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleEdit = (key: EmailTemplateKey) => {
    loadTemplate(key);
    setEditorOpen(true);
  };

  const handleEditorClose = (open: boolean) => {
    setEditorOpen(open);
    if (!open) {
      // Refresh list after editing
      loadTemplates();
    }
  };

  const customizedCount = filteredTemplates.filter((t) => t.isCustomized).length;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Page Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 flex items-center justify-center">
            <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-slate-800 dark:text-white">
                Email Templates
              </h1>
              {customizedCount > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {customizedCount} customized
                </span>
              )}
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Customize transactional emails sent from the platform. Changes take effect immediately.
            </p>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadTemplates()}
              disabled={isLoadingList}
            >
              <RefreshCw className={cn('w-3.5 h-3.5 mr-1.5', isLoadingList && 'animate-spin')} />
              Refresh
            </Button>
            <Link to="/settings">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                Settings
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Category Filter */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-3 border-b border-slate-100 dark:border-slate-800"
      >
        <div className="flex items-center gap-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setSelectedCategory(cat.value)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg font-medium transition-colors',
                selectedCategory === cat.value
                  ? 'bg-slate-800 dark:bg-slate-100 text-white dark:text-slate-900'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* Error Banner */}
        {listError && (
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-3 p-4 mb-6 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400"
          >
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm">{listError}</p>
          </motion.div>
        )}

        {/* Loading skeleton (before any templates are loaded) */}
        {isLoadingList && filteredTemplates.length === 0 && (
          <div className="space-y-4">
            {[0, 1].map((g) => (
              <div
                key={g}
                className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden"
              >
                <div className="flex items-center gap-3 px-4 py-3 bg-slate-50 dark:bg-slate-800/50">
                  <div className="w-8 h-8 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" />
                  <div className="flex-1">
                    <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded animate-pulse mb-1" />
                    <div className="h-3 w-64 bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
                  </div>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 animate-pulse"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                        <div className="h-4 w-16 bg-slate-100 dark:bg-slate-800 rounded-full" />
                      </div>
                      <div className="h-3 w-48 bg-slate-100 dark:bg-slate-800 rounded mb-3" />
                      <div className="h-4 w-24 bg-slate-100 dark:bg-slate-800 rounded-full" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state (after loading, no templates match the filter) */}
        {!isLoadingList && filteredTemplates.length === 0 && !listError && (
          <motion.div
            variants={itemVariants}
            className="flex flex-col items-center justify-center py-16 text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
              <LayoutGrid className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-base font-medium text-slate-700 dark:text-slate-300">
              No templates in this category
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Select a different category or view all templates.
            </p>
          </motion.div>
        )}

        {/* Sender-type grouped sections */}
        {filteredTemplates.length > 0 && (
          <div className="space-y-4">
            {respectlyTemplates.length > 0 && (
              <SenderGroup
                title="Templates sent from Respectly"
                description="Platform emails sent directly from Respectly to admins, team members, and account owners."
                icon={<Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />}
                accentClass="bg-blue-100 dark:bg-blue-900/40"
                templates={respectlyTemplates}
                isOpen={respectlyGroupOpen}
                onToggle={() => setRespectlyGroupOpen((v) => !v)}
                onEdit={handleEdit}
                isLoading={isLoadingList}
              />
            )}

            {customerTemplates.length > 0 && (
              <SenderGroup
                title="Templates sent from Respectly customers"
                description="Emails sent on behalf of your customers (tenants) to their contacts and end recipients."
                icon={<Users className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
                accentClass="bg-violet-100 dark:bg-violet-900/40"
                templates={customerTemplates}
                isOpen={customerGroupOpen}
                onToggle={() => setCustomerGroupOpen((v) => !v)}
                onEdit={handleEdit}
                isLoading={isLoadingList}
              />
            )}
          </div>
        )}

        {/* Info footer */}
        <motion.div
          variants={itemVariants}
          className="mt-8 p-4 rounded-lg border border-blue-100 dark:border-blue-900/40 bg-blue-50/50 dark:bg-blue-900/10"
        >
          <p className="text-xs text-blue-700 dark:text-blue-400 leading-relaxed">
            <strong>How it works:</strong> Customized templates override the system default at send time.
            Use <code className="font-mono bg-blue-100 dark:bg-blue-900/40 px-1 rounded">{'{{variableName}}'}</code> placeholders —
            they are replaced with real values when each email is sent.
            Resetting a template removes the customization and restores the system default.
          </p>
        </motion.div>
      </div>

      {/* Editor Panel (Sheet) */}
      {selectedKey && editorTemplate && !isLoadingDetail && (
        <EmailTemplateEditorPanel
          templateKey={selectedKey}
          definition={EMAIL_TEMPLATE_DEFINITIONS[selectedKey]}
          open={editorOpen}
          onOpenChange={handleEditorClose}
          initialSubject={editorTemplate.subject}
          initialHtml={editorTemplate.htmlContent}
          initialPreheader={editorTemplate.preheader}
          isCustomized={editorTemplate.isCustomized}
          onSave={async (subject, htmlContent, preheader) => {
            return saveTemplate(selectedKey, { subject, htmlContent, preheader });
          }}
          onReset={async () => {
            return resetTemplate(selectedKey);
          }}
          onTestSend={async (to) => {
            return sendTestEmail(selectedKey, to);
          }}
          isSaving={isSaving}
          isResetting={isResetting}
          isSendingTest={isSendingTest}
        />
      )}

      {/* Loading indicator while fetching template detail */}
      {isLoadingDetail && editorOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/20 dark:bg-black/40 z-50">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Loading template…
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
}
